import React from 'react'
import { CheckCircle2, AlertCircle, Clock, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { POST_STATUS, type PostStatus } from '@/constants/socialPostStatus'

interface StatusTransitionProps {
  status: PostStatus
  className?: string
  showLabel?: boolean
}

export default function StatusTransition({ 
  status, 
  className,
  showLabel = true
}: StatusTransitionProps) {
  const getStatusConfig = (status: PostStatus) => {
    switch (status) {
      case POST_STATUS.PROCESSING:
        return {
          icon: Loader2,
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-100 dark:bg-yellow-900/20',
          message: 'Publishing...',
          animate: true
        }
      case POST_STATUS.POSTED:
        return {
          icon: CheckCircle2,
          color: 'text-green-600',
          bgColor: 'bg-green-100 dark:bg-green-900/20',
          message: 'Published',
          animate: false
        }
      case POST_STATUS.ERROR:
        return {
          icon: AlertCircle,
          color: 'text-red-600',
          bgColor: 'bg-red-100 dark:bg-red-900/20',
          message: 'Failed',
          animate: false
        }
      case POST_STATUS.SCHEDULED:
        return {
          icon: Clock,
          color: 'text-blue-600',
          bgColor: 'bg-blue-100 dark:bg-blue-900/20',
          message: 'Scheduled',
          animate: false
        }
      default:
        return {
          icon: Clock,
          color: 'text-gray-600',
          bgColor: 'bg-gray-100 dark:bg-gray-900/20',
          message: status,
          animate: false
        }
    }
  }

  const config = getStatusConfig(status)
  const Icon = config.icon

  return (
    <div
      className={cn(
        "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full transition-all duration-300",
        config.bgColor,
        status === POST_STATUS.PROCESSING && "animate-pulse",
        className
      )}
    >
      <Icon 
        className={cn(
          "h-3.5 w-3.5", 
          config.color, 
          config.animate && "animate-spin"
        )} 
      />
      {showLabel && (
        <span className={cn("text-xs font-medium", config.color)}>
          {config.message}
        </span>
      )}
    </div>
  )
}